/**
 * Primary hook for consuming feature flags in React components.
 *
 * Evaluates flags using the server function and TanStack Query,
 * with automatic context building from the current user session.
 *
 * @see specs/feature-flags-system.md
 */
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"
import { usePermissions } from "@/hooks/usePermissions"
import { flagQueryKeys } from "@/lib/queryKeys"
import type { EvaluationSource } from "../types/flagTypes"
import { evaluateFlagFn, type EvaluationResult } from "../server/flagEvaluation"

/** Stale time for flag evaluations (5 seconds) */
const FLAG_STALE_TIME_MS = 5_000

/** GC time for flag evaluations (30 seconds) */
const FLAG_GC_TIME_MS = 30_000

export type UseFeatureFlagOptions = {
  /** Country ID override for evaluation context */
  countryId?: string
  /** Store ID override for evaluation context */
  storeId?: string
  /** Whether the query is enabled (default: true) */
  enabled?: boolean
}

export type UseFeatureFlagReturn<T> = {
  /** The evaluated flag value */
  value: T
  /** Whether the flag is currently loading */
  isLoading: boolean
  /** Whether initial data has loaded at least once */
  isReady: boolean
  /** Error if evaluation failed */
  error: Error | null
  /** How the value was determined */
  source: EvaluationSource | null
  /** Human-readable explanation of why this value was returned */
  reason: string | null
  /** Bucket value if rollout was evaluated */
  bucket: number | undefined
  /** Manually refetch the flag value */
  refetch: () => void
}

/**
 * Evaluate a feature flag for the current user.
 *
 * Uses TanStack Query with automatic cache invalidation via SSE.
 * Context is built from usePermissions() and optional overrides.
 *
 * @param flagKey - The flag key to evaluate
 * @param defaultValue - Value to return if flag not found or evaluation fails
 * @param options - Optional context overrides and query options
 *
 * @example
 * ```tsx
 * // Boolean flag
 * const { value: showNewUI, isLoading } = useFeatureFlag("new-ui", false)
 *
 * // String flag
 * const { value: theme } = useFeatureFlag("theme-variant", "default")
 *
 * // Number flag with context override
 * const { value: maxItems } = useFeatureFlag("max-items", 10, {
 *   storeId: selectedStoreId,
 * })
 *
 * // Conditional rendering
 * if (showNewUI) {
 *   return <NewDashboard />
 * }
 * return <OldDashboard />
 * ```
 */
export function useFeatureFlag<T extends boolean | string | number>(
  flagKey: string,
  defaultValue: T,
  options: UseFeatureFlagOptions = {},
): UseFeatureFlagReturn<T> {
  const { countryId, storeId, enabled = true } = options
  const { user, isLoading: isLoadingUser } = usePermissions()

  // Build context for query key (must be stable for caching)
  const contextForKey = useMemo(
    () => ({
      userId: user?.id ?? "",
      countryId,
      storeId,
    }),
    [user?.id, countryId, storeId],
  )

  const queryKey = flagQueryKeys.evaluate(flagKey, contextForKey)

  const {
    data,
    isLoading: isQueryLoading,
    isFetched,
    error,
    refetch: queryRefetch,
  } = useQuery<EvaluationResult<T>>({
    queryKey,
    queryFn: async () => {
      const result = await evaluateFlagFn({
        data: {
          flagKey,
          defaultValue,
          context: {
            countryId,
            storeId,
          },
        },
      })
      return result as EvaluationResult<T>
    },
    enabled: enabled && !!user && !isLoadingUser,
    staleTime: FLAG_STALE_TIME_MS,
    gcTime: FLAG_GC_TIME_MS,
    // Return default value while loading
    placeholderData: {
      flagKey,
      value: defaultValue,
      source: "default" as const,
      reason: "Loading flag evaluation",
    },
  })

  const refetch = useCallback(() => {
    queryRefetch()
  }, [queryRefetch])

  // Compute final values
  const isLoading = isLoadingUser || isQueryLoading
  const isReady = isFetched && !isLoadingUser

  return {
    value: data?.value ?? defaultValue,
    isLoading,
    isReady,
    error: error instanceof Error ? error : null,
    source: data?.source ?? null,
    reason: data?.reason ?? null,
    bucket: data?.bucket,
    refetch,
  }
}

/**
 * Evaluate multiple feature flags at once.
 *
 * Useful when a component needs several flags. Each flag is evaluated
 * independently with its own cache entry.
 *
 * @example
 * ```tsx
 * const flags = useFeatureFlags({
 *   showNewUI: { key: "new-ui", defaultValue: false },
 *   maxItems: { key: "max-items", defaultValue: 10 },
 *   theme: { key: "theme-variant", defaultValue: "default" },
 * })
 *
 * // Access individual flags
 * if (flags.showNewUI.value) { ... }
 * ```
 */
export function useFeatureFlags<
  T extends Record<string, { key: string; defaultValue: boolean | string | number }>,
>(
  flagConfigs: T,
  options: UseFeatureFlagOptions = {},
): {
  [K in keyof T]: UseFeatureFlagReturn<T[K]["defaultValue"]>
} {
  // Note: This hook calls useFeatureFlag for each flag, which is fine
  // because React guarantees stable hook ordering. Each flag gets its
  // own cache entry and can be invalidated independently.
  //
  // For performance, consider batching evaluations in a future optimization.

  const entries = Object.entries(flagConfigs) as Array<
    [keyof T, { key: string; defaultValue: boolean | string | number }]
  >

  const results: Record<string, UseFeatureFlagReturn<boolean | string | number>> = {}

  for (const [name, config] of entries) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    results[name as string] = useFeatureFlag(config.key, config.defaultValue, options)
  }

  return results as { [K in keyof T]: UseFeatureFlagReturn<T[K]["defaultValue"]> }
}

/**
 * Prefetch a flag value to warm the cache.
 *
 * Useful for prefetching flags that will be needed soon,
 * e.g., before navigating to a route that uses them.
 *
 * @example
 * ```tsx
 * const prefetch = usePrefetchFlag()
 *
 * // Prefetch before navigation
 * const handleNavigation = () => {
 *   prefetch("new-dashboard", false)
 *   router.navigate("/dashboard")
 * }
 * ```
 */
export function usePrefetchFlag() {
  const queryClient = useQueryClient()
  const { user } = usePermissions()

  return useCallback(
    async <T extends boolean | string | number>(
      flagKey: string,
      defaultValue: T,
      options: Pick<UseFeatureFlagOptions, "countryId" | "storeId"> = {},
    ): Promise<void> => {
      if (!user) return

      const contextForKey = {
        userId: user.id,
        countryId: options.countryId,
        storeId: options.storeId,
      }

      const queryKey = flagQueryKeys.evaluate(flagKey, contextForKey)

      await queryClient.prefetchQuery({
        queryKey,
        queryFn: async () => {
          return evaluateFlagFn({
            data: {
              flagKey,
              defaultValue,
              context: {
                countryId: options.countryId,
                storeId: options.storeId,
              },
            },
          })
        },
        staleTime: FLAG_STALE_TIME_MS,
      })
    },
    [queryClient, user],
  )
}
